import { FunctionComponent, useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'
import moment, { Moment } from 'moment/moment'
import UserInput from '@/components/_custom/UserInput/userInput'
import { useTranslation } from 'react-i18next'
import DatePickerComponent from '@/components/_custom/Picker/datePicker/datePicker'
import TextArea from '@/components/_custom/TextArea/textArea'
import styles from './editClosingDay.module.scss'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { ClosingDay } from '@/types/ClosingDays'
import { CREATE_CLOSING_DAY, UPDATE_CLOSING_DAY } from '@/modules/API/daycare/closingDayHandler'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'
import { Daycare } from '@/types/Daycare'
import { Autocomplete, Chip, TextField } from '@mui/material'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { useAuth } from '@/modules/context/AuthContext.tsx'
import { Role } from '@/types/Role.ts'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple.tsx'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select.ts'
import { useDialog } from '@/hooks/useDialog/useDialog'
import SelectMultipleDateDialogComponent from '@/container/planning/Dialog/occasionalSlot/occasionalSlotForm/components/SelectMultipleDateDialog/selectMultipleDateDialog'

interface EditClosingDayProps {
    create: boolean
    daycareId: number
    setTitle: (title: string) => void
    title: string
    setIsClosingDay?: (isClosingDay: boolean) => void
    isClosingDay?: boolean
    handleClose: () => void
    refetchAllEvents: () => Promise<void>
    closingDay?: ClosingDay
    closingDays?: ClosingDay[]
    daycare?: Daycare
    dateStart: string
    setDateStart: (date: string) => void
}

const EditClosingDay: FunctionComponent<EditClosingDayProps> = ({
    create,
    daycareId,
    handleClose,
    setTitle,
    title,
    setIsClosingDay,
    isClosingDay,
    refetchAllEvents,
    closingDay,
    closingDays,
    dateStart,
    setDateStart
}: EditClosingDayProps) => {
    const { user } = useAuth()

    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [dateEnd, setDateEnd] = useState<string>(
        !create ? moment(closingDay?.endDate, 'DD-MM-YYYY').format('DD-MM-YYYY') : moment().format('DD-MM-YYYY')
    )
    const [message, setMessage] = useState<string>(closingDay?.closingDaysMessage || '')

    const [chooseDate, setChooseDate] = useState<boolean>(false)
    const [dateList, setDateList] = useState<string[]>([])

    const [selectedDaycare, setSelectedDaycare] = useState<string[]>([user?.currentDaycare?.id.toString() || ''])

    const daycareOptions: SelectOptionsDeprecated = [
        {
            options:
                user?.daycares.map((daycare: Daycare): SelectOptionDeprecated => {
                    return {
                        name: daycare.name,
                        value: daycare.id.toString()
                    }
                }) || []
        }
    ]

    async function createClosingDay(): Promise<void> {
        if (!chooseDate) {
            if (user?.role === Role.trustee && selectedDaycare.length >= 1) {
                for (const selectedDaycareKey of selectedDaycare) {
                    await createOneClosingDay({
                        variables: {
                            daycare_id: Number(selectedDaycareKey),
                            closing_day_reason: title,
                            start_date: dateStart,
                            end_date: dateEnd,
                            closing_day_message: message
                        }
                    })
                }
            } else {
                await createOneClosingDay({
                    variables: {
                        daycare_id: daycareId,
                        closing_day_reason: title,
                        start_date: dateStart,
                        end_date: dateEnd,
                        closing_day_message: message
                    }
                })
            }
        } else {
            const sortedDates: string[] = [...dateList].sort((a: string, b: string) =>
                moment(a, 'DD-MM-YYYY').diff(moment(b, 'DD-MM-YYYY'))
            )

            const groupedDates: { start: string; end: string }[] = []
            let startDate: string = sortedDates[0]
            let previousDate: Moment = moment(startDate, 'DD-MM-YYYY').clone()

            for (let i: number = 1; i <= sortedDates.length; i++) {
                const currentDate: Moment | null = sortedDates[i] ? moment(sortedDates[i], 'DD-MM-YYYY') : null

                if (
                    currentDate &&
                    (currentDate.diff(previousDate, 'days') === 1 || currentDate.diff(previousDate, 'days') === 0)
                ) {
                    previousDate = currentDate
                } else {
                    groupedDates.push({
                        start: startDate,
                        end: previousDate.format('DD-MM-YYYY')
                    })
                    if (currentDate) {
                        startDate = currentDate.format('DD-MM-YYYY')
                        previousDate = currentDate
                    }
                }
            }

            if (user?.role === Role.trustee && selectedDaycare.length >= 1) {
                for (const selectedDaycareKey of selectedDaycare) {
                    for (const { start, end } of groupedDates) {
                        await createOneClosingDay({
                            variables: {
                                daycare_id: Number(selectedDaycareKey),
                                closing_day_reason: title,
                                start_date: start,
                                end_date: end,
                                closing_day_message: message
                            }
                        })
                    }
                }
            } else {
                for (const { start, end } of groupedDates) {
                    await createOneClosingDay({
                        variables: {
                            daycare_id: daycareId,
                            closing_day_reason: title,
                            start_date: start,
                            end_date: end,
                            closing_day_message: message
                        }
                    })
                }
            }
        }

        setTitle('')
        if (setIsClosingDay) setIsClosingDay(false)

        showNotificationSuccess(t('closingDayCreated'))
        handleClose()
    }

    const [createOneClosingDay, { loading: loadingCreate }] = useMutation(CREATE_CLOSING_DAY, {
        onCompleted: () => {
            refetchAllEvents()
        },
        onError: (error) => {
            if (error.message.split('Message: ')[0] === 'Closing day overlaps') {
                showNotificationError(t('closingDayOverlaps'))
            } else {
                showNotificationError(t('errorCreatingClosingDay'))
            }
        }
    })

    function handleCheckChooseDate(checked: boolean): void {
        if (checked) openSelectMultipleDateDialog()
        setChooseDate(checked)
    }

    function handleDateRemove(dateToRemove: string): void {
        setDateList(dateList.filter((date) => date !== dateToRemove))
    }

    const [updateOneClosingDay, { loading: loadingUpdate }] = useMutation(UPDATE_CLOSING_DAY, {
        variables: {
            closing_day_id: closingDay?.id,
            start_date: dateStart,
            end_date: dateEnd,
            closing_day_reason: title,
            closing_day_message: message,
            daycare_id: daycareId
        },
        onCompleted: () => {
            refetchAllEvents()

            setTitle('')

            showNotificationSuccess(t('closingDayUpdated'))
            handleClose()
        },
        onError: (error) => {
            if (error.message.split('Message: ')[0] === 'Closing day overlaps') {
                showNotificationError(t('closingDayOverlaps'))
            } else {
                showNotificationError(t('errorUpdatingClosingDay'))
            }
        }
    })

    useEffect(() => {
        if (moment(dateStart, 'DD-MM-YYYY').isAfter(moment(dateEnd, 'DD-MM-YYYY'))) {
            setDateEnd(dateStart)
        }
    }, [dateStart])

    useEffect(() => {
        if (closingDay?.closingDaysReason) setTitle(closingDay.closingDaysReason)
        if (closingDay?.startDate) setDateStart(moment(closingDay.startDate, 'DD-MM-YYYY').format('DD-MM-YYYY'))
    }, [])

    const disabled: boolean = create
        ? title === '' || dateStart === '' || dateEnd === '' || (chooseDate && dateList.length === 0)
        : (title === closingDay?.closingDaysReason &&
              message === closingDay?.closingDaysMessage &&
              dateStart === moment(closingDay?.startDate, 'DD-MM-YYYY').format('DD-MM-YYYY') &&
              dateEnd === moment(closingDay?.endDate, 'DD-MM-YYYY').format('DD-MM-YYYY')) ||
          loadingCreate ||
          loadingUpdate

    const { dialog: SelectMultipleDateDialog, openDialog: openSelectMultipleDateDialog } = useDialog({
        Component: SelectMultipleDateDialogComponent,
        props: () => ({
            dateList,
            setDateList,
            startDate: dateStart,
            closingDays: closingDays || []
        }),
        dialogTitle: false
    })

    return (
        <div className={styles.Form}>
            <div className={styles.InputContainer}>
                <div className={styles.Input}>
                    <UserInput
                        label={t('title')}
                        value={title}
                        setValue={setTitle}
                        required
                        className={styles.UserInput}
                    />
                </div>
                {create && setIsClosingDay && (
                    <div className={styles.CheckBoxContainer}>
                        <Checkbox
                            text={t('isClosingDay')}
                            checked={isClosingDay}
                            onChange={() => setIsClosingDay(!isClosingDay)}
                        />
                    </div>
                )}
                {create && user?.role === Role.trustee && (
                    <div className={styles.Input}>
                        <SelectMultiple
                            label={t('myDaycares')}
                            selectOptions={daycareOptions}
                            selectValues={selectedDaycare}
                            setSelectValues={setSelectedDaycare}
                            className={styles.UserInput}
                            maxToShow={5}
                        />
                    </div>
                )}
                {chooseDate ? (
                    <div className={styles.Input}>
                        <div className={styles.UserInput}>
                            <Autocomplete
                                multiple
                                options={dateList}
                                limitTags={3}
                                value={dateList}
                                onChange={(_event, newValue) => setDateList(newValue)}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <div key={option}>
                                            <Chip
                                                label={option}
                                                {...getTagProps({ index })}
                                                onDelete={() => handleDateRemove(option)}
                                            />
                                        </div>
                                    ))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant='outlined'
                                        rows={1}
                                        multiline={true}
                                        label={t('dateList')}
                                    />
                                )}
                                className={styles.Autocomplete}
                                onOpen={openSelectMultipleDateDialog}
                            />
                        </div>
                    </div>
                ) : (
                    <div className={styles.Input}>
                        <div className={styles.UserInput}>
                            <DatePickerComponent
                                value={dateStart}
                                setValue={setDateStart}
                                label={t('dateStart')}
                                className={styles.FullWidth}
                            />
                        </div>
                        <div className={styles.UserInput}>
                            <DatePickerComponent
                                value={dateEnd}
                                setValue={setDateEnd}
                                label={t('dateEnd')}
                                className={styles.FullWidth}
                                minDate={dateStart}
                            />
                        </div>
                    </div>
                )}
                <div className={styles.CheckBoxContainer}>
                    <Checkbox text={t('chooseDate')} checked={chooseDate} onChange={handleCheckChooseDate} />
                </div>
                <div className={styles.Input}>
                    <TextArea
                        value={message}
                        className={styles.TextArea}
                        name={t('message')}
                        placeholder={t('closingDayDescription')}
                        onChange={setMessage}
                    />
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                    <ButtonTextIcon
                        type={create ? 'add' : 'update'}
                        onClick={create ? createClosingDay : updateOneClosingDay}
                        className={styles.Button}
                        disabled={disabled}
                    />
                </div>
            </div>
            {SelectMultipleDateDialog}
        </div>
    )
}

export default EditClosingDay
